import { useEffect, useMemo, useRef, useState } from "react";
import DriverSummaryCard from "./DriverSummaryCard.jsx";
import ProductCard from "./ProductCard.jsx";
import {
  getLandstarSelectionRule,
  isLandstarPersonalizationEnabled
} from "../utils/helpers.js";

const PRODUCTS_PER_PAGE = 4;

function ProductSelectionStep({
  driverProfile,
  availableProducts,
  selectedProducts,
  selectedProductIds,
  productSelections,
  selectionLimit,
  onSelectionChange,
  onSelect,
  onBack,
  onContinue,
  error
}) {
  const [page, setPage] = useState(1);
  const productSectionRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [availableProducts, driverProfile?.driverCode]);

  useEffect(() => {
    if (page === 1) {
      return;
    }

    productSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(availableProducts.length / PRODUCTS_PER_PAGE));
  const personalizationEnabled = isLandstarPersonalizationEnabled(driverProfile);
  const selectionRule = getLandstarSelectionRule(driverProfile);
  const isRequiredKit = selectionRule.mode === "required_kit";
  const visibleProducts = useMemo(() => {
    const start = (page - 1) * PRODUCTS_PER_PAGE;
    return availableProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [availableProducts, page]);

  return (
    <div className="landstar-step-content">
      <DriverSummaryCard driverProfile={driverProfile} />

      <div className="landstar-step-main" ref={productSectionRef}>
        <div className="landstar-section-heading">
          <p className="landstar-kicker">Eligible Rewards</p>
          <h3>
            {isRequiredKit
              ? "Configure your 18-year jacket and pants kit"
              : selectionLimit === 1
                ? "Choose one item from your tier"
                : "Choose your reward items"}
          </h3>
          <p className="landstar-helper-text">
            {isRequiredKit
              ? "Both items are included. Choose the jacket and pants sizes independently before continuing."
              : selectionLimit === 1
              ? "This tier allows one reward selection. Configure it fully before continuing."
              : "You can choose more than one reward in this tier. Configure each selected item before continuing."}
          </p>
          {selectedProducts.length ? (
            <p className="landstar-helper-text">
              {isRequiredKit
                ? `${selectedProducts.length} kit items included`
                : `${selectedProducts.length} reward${selectedProducts.length === 1 ? "" : "s"} selected`}
            </p>
          ) : null}
        </div>

        <div
          className={`landstar-product-grid${
            visibleProducts.length === 1 ? " is-single-product" : ""
          }`}
        >
          {visibleProducts.map((product) => {
            const normalizedProductId = String(product.id);

            return (
            <ProductCard
              key={normalizedProductId}
              product={product}
              eligibilityBadge={driverProfile?.tierYear || ""}
              selected={selectedProductIds.includes(normalizedProductId)}
              selectionLimit={selectionLimit}
              hasReachedSelectionLimit={
                selectionLimit === 1 &&
                selectedProductIds.length >= selectionLimit &&
                !selectedProductIds.includes(normalizedProductId)
              }
              selection={productSelections[normalizedProductId] || {}}
              personalizationEnabled={personalizationEnabled}
              lockedSelected={isRequiredKit}
              onSelectionChange={onSelectionChange}
              onSelect={onSelect}
            />
            );
          })}
        </div>

        {error ? <p className="landstar-error">{error}</p> : null}

        {availableProducts.length > PRODUCTS_PER_PAGE ? (
          <div className="landstar-pagination-bar">
            <p className="landstar-helper-text">
              Showing page {page} of {totalPages}
            </p>
            <div className="landstar-pagination-actions">
              <button
                type="button"
                className="landstar-secondary-button"
                disabled={page === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="landstar-secondary-button"
                disabled={page === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <hr className="landstar-section-divider" />

      <div className="landstar-inline-actions">
        <button type="button" className="landstar-secondary-button" onClick={onBack}>
          Return to Code Entry
        </button>
        <button type="button" className="landstar-primary-button" onClick={onContinue}>
          Continue to Shipping
        </button>
      </div>
    </div>
  );
}

export default ProductSelectionStep;
